import { FireMethod, CalculationMode } from '../enums';
import { FireScenario } from '../entities/FireScenario';
import { MethodResult } from '../entities/MethodResult';
import { PlanResult } from '../entities/PlanResult';
import { AgeYears } from '../value-objects/AgeYears';
import { MoneyAmount } from '../value-objects/MoneyAmount';
import { FireCalculator } from './FireCalculator';
import { SimulationEngine } from './SimulationEngine';

const CLASSICAL_HORIZON = 100;
const DWZ_HORIZON = 90;

export class PlanService {
  private readonly calculator: FireCalculator;
  private readonly engine: SimulationEngine;

  constructor(
    calculator?: FireCalculator,
    engine?: SimulationEngine,
  ) {
    this.calculator = calculator ?? new FireCalculator();
    this.engine = engine ?? new SimulationEngine();
  }

  runFullPlan(
    scenario: FireScenario,
    mode: CalculationMode = CalculationMode.byRetirementAge,
  ): PlanResult {
    const classicalFire = this.computeMethod(
      scenario,
      FireMethod.classicalFire,
      CLASSICAL_HORIZON,
      mode,
    );

    const dieWithZero = this.computeMethod(
      scenario,
      FireMethod.dieWithZero,
      DWZ_HORIZON,
      mode,
    );

    return new PlanResult({
      [FireMethod.classicalFire]: classicalFire,
      [FireMethod.dieWithZero]: dieWithZero,
    });
  }

  private computeMethod(
    scenario: FireScenario,
    method: FireMethod,
    horizonAge: number,
    mode: CalculationMode,
  ): MethodResult {
    let requiredMonthlyInvestment: MoneyAmount;
    let solvedRetirementAge: AgeYears | null;
    let effectiveRetirementAge: number;
    let yearsToTarget: number;

    if (mode === CalculationMode.byRetirementAge) {
      requiredMonthlyInvestment = this.calculator.calculateRequiredMonthlyInvestment(
        scenario,
        method,
      );
      effectiveRetirementAge = scenario.retirementAge.value;
      yearsToTarget = scenario.yearsToTarget;
      solvedRetirementAge = null;
    } else {
      requiredMonthlyInvestment = scenario.monthlyContribution;
      solvedRetirementAge = this.calculator.solveForRetirementAge(
        scenario,
        method,
      );
      effectiveRetirementAge = solvedRetirementAge.value;
      yearsToTarget = solvedRetirementAge.value - scenario.currentAge.value;
    }

    const fireNumber =
      method === FireMethod.classicalFire
        ? this.calculator.calculateFireNumber(scenario)
        : this.calculator.calculateDieWithZeroAtAge(
            scenario,
            effectiveRetirementAge,
          );

    const futureNominalRaw =
      fireNumber.value * Math.pow(1 + scenario.inflation.value, yearsToTarget);
    const futureNominalValue = Math.round(futureNominalRaw * 100) / 100;

    const yearResults = this.engine.simulate(
      scenario,
      method,
      new MethodResult({
        fireNumber: fireNumber.value,
        requiredMonthlyInvestment: requiredMonthlyInvestment.value,
        futureNominalValue,
        solvedRetirementAge,
      }),
      horizonAge,
    );

    return new MethodResult({
      fireNumber: fireNumber.value,
      requiredMonthlyInvestment: requiredMonthlyInvestment.value,
      futureNominalValue,
      solvedRetirementAge,
      yearResults,
    });
  }
}